import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generateMemberSteps, generateStep } from '../utils/progressSteps';
import {
  createApplication,
  getForeignTaxInformation,
  getMemberDetails,
  getReferenceData,
  retrieveApplication,
  saveForLaterEmail,
  securityCodeValidation,
  sendSMS,
  submitApplication,
  updateAccountDetails,
  updatePersonalDetails,
} from './asyncActions';
import { RootState } from './store';
import {
  ApplicantProgressState,
  CommonState,
  ReferenceDataState,
  ProgressStepState,
} from './types';

const initialState: CommonState = {
  isTablet: Boolean(window.innerWidth <= 768),
  accountType: 'EA',
  accountTypeRoute: 'everyday',
  currentStep: 'Landing',
  currentStepIndex: -1,
  nextStep: '',
  nextStepIndex: -1,
  utmCampaignCode: '',
  isMinorApplication: null,
  isParentGuardianPresent: null,
  applicationType: '',
  applicationCreated: false,
  referenceNumber: '',
  CracaReferenceNumber: '',
  progress: [],
  errorBannerType: '',
  pageLoading: false,
  interestRateChanged: '',
  excludedStates: ['ACT', 'NT', 'SA', 'TAS', 'VIC', 'WA'],
  referenceData: {
    accountCardType: [],
    applicationTypes: [],
    birthCertificateRegistrationDistricts: [],
    cardColours: [],
    cardTypes: [],
    depositMethods: [],
    employmentStatus: [],
    exemptTaxFileNumberOptions: [],
    genders: [],
    interestPaidToTypes: [],
    interestPaymentOptions: [],
    interestPaymentPeriodAllExceptAnnuallyOptions: [],
    interestPaymentPeriodAllExceptMaturityOptions: [],
    interestPaymentPeriodMatuirtyMonthlyOptions: [],
    interestPaymentPeriodMatuirtyOnlyOptions: [],
    investmentTermTypes: [],
    mailingDestination: [],
    medicareCardColours: [],
    methodOfOperation: [],
    numberOfCardsOptions: [],
    numberOfDependentsOptions: [],
    postalAddressType: [],
    productCodes: [],
    purposeOfAccount: [],
    residentialStatus: [],
    sourceOfFunds: [],
    addressTypes: [],
    states: [],
    titles: [],
    occupations: [],
    countries: [],
    foreignTaxCountries: [],
    yesOrNoOptions: [
      {
        value: 'false',
        label: 'No',
      },
      {
        value: 'true',
        label: 'Yes',
      },
    ],
    yesOrNoInvertedOptions: [
      {
        value: 'true',
        label: 'No',
      },
      {
        value: 'false',
        label: 'Yes',
      },
    ],
    offsetAccountRequiredOptions: [
      {
        value: 'false',
        label: 'No thanks',
      },
      {
        value: 'true',
        label: 'Yes',
      },
    ],
    optionalCardOptions: [
      {
        value: 'false',
        label: 'No thanks',
      },
      {
        value: 'true',
        label: 'Yes',
      },
    ],
    declarationsOptions: [
      {
        value: 'noticeOfMeetings',
        label: 'Notice of meetings',
      },
      {
        value: 'annualReports',
        label: 'Annual reports',
      },
    ],
    identityTypeOptions: [
      {
        value: 'Driver licence',
        label: 'Driver licence',
      },
      {
        value: 'Passport',
        label: 'Passport',
      },
      {
        value: 'Medicare card',
        label: 'Medicare card',
      },
      {
        value: 'Birth certificate',
        label: 'Birth certificate',
      },
    ],
    provideTFNOptions: [
      {
        value: 'Provide TFN',
        label: 'Provide TFN',
      },
      {
        value: 'I have an exemption code',
        label: 'I am exempt',
      },
      {
        value: 'Skip',
        label: 'Skip for now',
      },
    ],
    taxResidentAusOnlyOptions: [
      {
        value: 'false',
        label: 'Yes',
      },
      {
        value: 'true',
        label: 'No, only Australia',
      },
    ],
    accountTypes: [
      {
        value: 'EA',
        label: 'Everyday',
        route: 'everyday',
      },
      {
        value: 'OS',
        label: 'Online Savings',
        route: 'online-savings',
      },
      {
        value: 'SSA',
        label: 'Smart Saver',
        route: 'smart-saver',
      },
      {
        value: 'SMI',
        label: 'Special Monthly Interest',
        route: 'special-monthly-interest',
      },
      {
        value: 'FIA',
        label: 'Term Deposit',
        route: 'term-deposit',
      },
      {
        value: 'CC',
        label: 'Credit Card',
        route: 'credit-card',
      },
      {
        value: 'MA',
        label: 'New Customer',
        route: 'new-customer',
      },
    ],
    additionalAccounts: [
      {
        accountType: 'EA',
        additionalAccounts: ['SSA', 'OS'],
      },
      {
        accountType: 'SMI',
        additionalAccounts: ['SSA'],
      },
      {
        accountType: 'SSA',
        additionalAccounts: ['EA'],
      },
      {
        accountType: 'OS',
        additionalAccounts: ['EA'],
      },
    ],
    // maybe no longer required
    additionalAccountsOptions: [
      {
        value: 'OS',
        label: 'Online Savings',
      },
      {
        value: 'SSA',
        label: 'Smart Saver',
      },
    ],
    applicationTypeOptions: [
      {
        value: 'Single',
        label: 'Single',
      },
      {
        value: 'Joint',
        label: 'Joint',
      },
    ],
    interestingFactsOptions: [
      {
        year: 1903,
        name: 'younger than us',
      },
      {
        year: 2003,
        name: 'younger than our Charitable Foundation',
      },
      {
        year: 1828,
        name: "younger than the Hunter Valley's oldest vineyard",
      },
      {
        year: 1819,
        name: "younger than Newcastle's Bogey Hole",
      },
      {
        year: 2007,
        name: "since the east coast 'Pasha Bulker' storm",
      },
      {
        year: 1918,
        name: 'younger than Hunter Surf Life Saving',
      },
      {
        year: 1884,
        name: 'younger than Northern NSW Football',
      },
      {
        year: 1988,
        name: "younger than Australia's first plastic bank note",
      },
      {
        year: 1932,
        name: 'younger than the Sydney Harbour Bridge',
      },
      {
        year: 1851,
        name: 'younger than Bathurst goldfields',
      },
    ],
    postalAddressTypeOptions: [
      {
        value: 'Address',
        label: 'Address',
      },
      {
        value: 'PO Box',
        label: 'Post Office Box',
      },
    ],
  },
};

const generateApplicantSteps = (
  state,
  applicantIndex: number,
  existingMember: boolean,
  haveMoreApplicant: boolean,
  isMinorApplication: boolean
): ProgressStepState[] => {
  const newMemberStepsArray = [
    'About you',
    'Verify your identity',
    'Tax details',
    'Account details',
  ];
  const existingMemberStepsArray = [
    'About you',
    'Account details'
  ];
  const creditcardNewMemberStepsArray = [
    'About you',
    'Verify your identity',
    'Tax details',
    'Financial details'
  ];
  const creditcardExistingMemberStepsArray = [
    'About you',
    'Financial details'
  ];
  const creditcardAdditonalCardHolderNewMemberStepsArray = [
    'About you',
    'Verify your identity',
    'Tax details'
  ];
  const creditcardAdditonalCardHolderExistingMemberStepsArray = [
    'About you'
  ];
  const newMemberApplicationStepsArray = [
    'About you',
    'Verify your identity',
    'Tax details',
  ];

  let routeApplicantIndex = applicantIndex + 1;

  if (state.applicationType === 'Single') {
    let stepsArrays = existingMember
      ? existingMemberStepsArray
      : newMemberStepsArray;

    let stepsArraysCreditCard = existingMember
      ? creditcardExistingMemberStepsArray
      : creditcardNewMemberStepsArray;

    let stepsArraysCreditCardAdditonalCardHolder = existingMember
      ? creditcardAdditonalCardHolderExistingMemberStepsArray
      : creditcardAdditonalCardHolderNewMemberStepsArray;

    let isAddtionalCardHolder = applicantIndex > 0;

    let steps = generateMemberSteps(
      state.accountType === 'CC'
        ? isAddtionalCardHolder
          ? stepsArraysCreditCardAdditonalCardHolder
          : stepsArraysCreditCard
        : state.accountType === 'MA'
          ? newMemberApplicationStepsArray
          : stepsArrays,
      state.accountTypeRoute,
      routeApplicantIndex
    );

    const nextStep = generateStep(
      haveMoreApplicant
        ? 'Additional cardholder'
        : 'Review & submit',
      steps.length + 1,
      state.accountTypeRoute,
      routeApplicantIndex
    );

    return [...steps, nextStep];
  } else if (state.applicationType === 'Joint') {
    let steps = generateMemberSteps(
      existingMember ? existingMemberStepsArray : newMemberStepsArray,
      state.accountTypeRoute,
      routeApplicantIndex
    );

    //second applicant of Term Deposit doesn't have Account details step
    if (state.accountType === 'FIA' && applicantIndex > 0) {
      steps = steps.filter((s) => s.name !== 'Account details');
    }

    let nextStep = generateStep(
      applicantIndex === 0
        ? isMinorApplication
          ? 'Child applicant'
          : 'Next applicant'
        : 'Review & submit',
      steps.length + 1,
      state.accountTypeRoute,
      routeApplicantIndex
    );
    return [...steps, nextStep];
  }

  return [];
};

const getProgressNextStep = (
  progress,
  currentApplicantIndex,
  currentStepIndex
): ProgressStepState => {
  let progressNextStep;

  if (
    progress.length > currentApplicantIndex + 1 &&
    progress[currentApplicantIndex].applicantSteps?.length ===
    currentStepIndex + 1
  ) {
    progressNextStep = progress[currentApplicantIndex + 1]?.applicantSteps?.[0];
  } else {
    progressNextStep =
      progress[currentApplicantIndex]?.applicantSteps?.[currentStepIndex + 1];
  }

  return progressNextStep;
};

/* Reducers */
export const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setReferenceData: (state, action: PayloadAction<ReferenceDataState>) => {
      state.referenceData = action.payload;
    },
    setIsTablet: (state, action: PayloadAction<boolean>) => {
      state.isTablet = action.payload;
    },
    setAccountType: (state, action: PayloadAction<string>) => {
      state.accountType = action.payload;
    },
    setAccountTypeRoute: (state, action: PayloadAction<string>) => {
      state.accountTypeRoute = action.payload;
    },
    setCurrentStep: (state, action: PayloadAction<string>) => {
      state.currentStep = action.payload;
    },
    setCurrentStepIndex: (state, action: PayloadAction<number>) => {
      const currentApplicantIndex = action.payload;

      // set currentStepIndex
      if (state.currentStep === 'Landing') {
        state.currentStepIndex = -1;
      } else {
        state.progress[currentApplicantIndex].applicantSteps.forEach(
          (item, index) => {
            if (state.currentStep === item.name) {
              state.currentStepIndex = index;
            }
          }
        );
      }

      // set nextStep & nextStepIndex for updatePersonalDetails API to pass next step on retrieval
      const progressNextStep = getProgressNextStep(
        state.progress,
        currentApplicantIndex,
        state.currentStepIndex
      );
      state.nextStep = progressNextStep?.name || '';
      state.nextStepIndex = progressNextStep?.step - 1 || -1;
    },
    setUtmCampaignCode: (state, action: PayloadAction<string>) => {
      state.utmCampaignCode = action.payload;
    },
    setIsMinorApplication: (state, action: PayloadAction<boolean | null>) => {
      state.isMinorApplication = action.payload;
    },
    setIsParentGuardianPresent: (
      state,
      action: PayloadAction<boolean | null>
    ) => {
      state.isParentGuardianPresent = action.payload;
    },
    setApplicationType: (state, action: PayloadAction<string>) => {
      state.applicationType = action.payload;
    },
    setApplicationCreated: (state, action: PayloadAction<boolean>) => {
      state.applicationCreated = action.payload;
    },
    setReferenceNumber: (state, action: PayloadAction<string>) => {
      state.referenceNumber = action.payload;
    },
    setProgress: (state, action: PayloadAction<ApplicantProgressState[]>) => {
      state.progress = action.payload;
    },
    initApplicantProgress: (state) => {
      // Init progress bar and set first applicant steps
      const isMinorApplication = state.isMinorApplication;
      let updatedProgress = state.progress;

      if (state.applicationType === 'Single') {
        updatedProgress = [
          {
            applicantName: 'Primary applicant',
            applicantUrl:
              '/' + state.accountTypeRoute + '/applicants/1/about-you',
            applicantStatus: 'current',
            applicantSteps: [],
          },
        ];
      } else if (state.applicationType === 'Joint') {
        updatedProgress = [
          {
            applicantName: isMinorApplication
              ? 'Parent/Guardian'
              : 'Applicant 1',
            applicantUrl:
              '/' + state.accountTypeRoute + '/applicants/1/about-you',
            applicantStatus: 'current',
            applicantSteps: [],
          },
          {
            applicantName: isMinorApplication
              ? 'Child applicant'
              : 'Applicant 2',
            applicantUrl:
              '/' + state.accountTypeRoute + '/applicants/2/about-you',
            applicantStatus: 'disabled',
            applicantSteps: [],
          },
        ];
      }

      state.progress = updatedProgress;
    },
    addApplicantToProgress: (state) => {
      let updatedProgress = state.progress;
      let nextApplicantNumber = updatedProgress.length + 1;
      let newApplicant = {
        applicantName: `Additional cardholder`,
        applicantUrl:
          '/' +
          state.accountTypeRoute +
          `/applicants/${nextApplicantNumber}/about-you`,
        applicantStatus: 'disabled',
        applicantSteps: [],
      };
      updatedProgress.push(newApplicant);
    },
    removeApplicantFromProgress: (state) => {
      let updatedProgress = state.progress;
      updatedProgress.pop();
    },
    setApplicantProgressSteps: (
      state,
      action: PayloadAction<{
        applicantIndex: number;
        existingMember: boolean; // Only set when updating next applicant steps
        haveMoreApplicant?: boolean;
      }>
    ) => {
      const applicantIndex = action.payload.applicantIndex;
      const existingMember = action.payload.existingMember;
      const haveMoreApplicant = action.payload.haveMoreApplicant;
      const isMinorApplication = state.isMinorApplication;

      let updatedProgress = state.progress;

      updatedProgress[applicantIndex].applicantSteps = generateApplicantSteps(
        state,
        applicantIndex,
        existingMember,
        haveMoreApplicant!,
        isMinorApplication!
      );

      state.progress = updatedProgress;
    },
    setRetrievalProgress: (
      state,
      action: PayloadAction<{
        applicantsNumber: number;
        currentApplicantIndex: number;
        retrievalApplicants;
      }>
    ) => {
      const { applicantsNumber, currentApplicantIndex, retrievalApplicants } =
        action.payload;

      let updatedProgress = state.progress;

      for (
        let applicantIndex = 0;
        applicantIndex < applicantsNumber;
        applicantIndex++
      ) {
        let thisApplicantSteps = generateApplicantSteps(
          state,
          applicantIndex,
          retrievalApplicants[applicantIndex].memberDetails.isExistingMember,
          applicantIndex < applicantsNumber - 1,
          state.isMinorApplication!
        );

        thisApplicantSteps = thisApplicantSteps.map((stepItem, stepIndex) => {
          const stepStatus =
            applicantIndex < currentApplicantIndex ||
              (applicantIndex === currentApplicantIndex &&
                stepIndex < state.currentStepIndex)
              ? 'available'
              : applicantIndex === currentApplicantIndex &&
                stepIndex === state.currentStepIndex
                ? 'current'
                : 'disabled';

          return {
            ...stepItem,
            status: stepStatus,
          };
        });

        let applicantName = '';
        if (state.isMinorApplication) {
          applicantName =
            applicantIndex === 0 ? 'Parent/Guardian' : 'Child applicant';
        } else if (state.accountType === 'CC') {
          applicantName =
            applicantIndex === 0
              ? 'Primary applicant'
              : 'Additional cardholder';
        } else {
          applicantName =
            applicantIndex === 0
              ? 'Primary applicant'
              : 'Applicant ' + (applicantIndex + 1);
        }

        updatedProgress.push({
          applicantName: applicantName,
          applicantUrl:
            '/' +
            state.accountTypeRoute +
            '/applicants/' +
            (applicantIndex + 1) +
            '/about-you',
          applicantStatus:
            applicantIndex < currentApplicantIndex
              ? 'available'
              : applicantIndex === currentApplicantIndex
                ? 'current'
                : 'disabled',
          applicantSteps: thisApplicantSteps,
        });
      }

      state.progress = updatedProgress;
    },
    setAddtionalCardHolderProgressStep: (state, action: PayloadAction<{ applicantIndex: number; stepName: string }>) => {
      let updatedProgress = state.progress;
      const currentApplicantIndex = action.payload.applicantIndex;

      updatedProgress[currentApplicantIndex].applicantSteps[state.currentStepIndex + 1] = generateStep(
        action.payload.stepName,
        state.progress[currentApplicantIndex].applicantSteps.length,
        state.accountTypeRoute,
        currentApplicantIndex + 1
      );
    },
    setProgressGoNext: (state, action: PayloadAction<number>) => {
      const currentApplicantIndex = action.payload;
      let updatedProgress = state.progress;

      // Set current step to 'available'
      if (state.currentStepIndex >= 0) {
        updatedProgress[currentApplicantIndex].applicantSteps[state.currentStepIndex].status = 'available';
      }

      // Set next step to 'available'
      if (updatedProgress.length > currentApplicantIndex + 1 &&
        updatedProgress[currentApplicantIndex].applicantSteps.length === state.currentStepIndex + 1) {
        // Go to first step of next applicant
        updatedProgress[currentApplicantIndex].applicantStatus = 'available';
        updatedProgress[currentApplicantIndex + 1].applicantStatus = 'current';
        updatedProgress[currentApplicantIndex + 1].applicantSteps[0].status = 'current';
      } else {
        // Go to next step of current applicant
        updatedProgress[currentApplicantIndex].applicantSteps[state.currentStepIndex + 1].status = 'current';
      }

      state.progress = updatedProgress;
    },
    setProgressGoBack: (state, action: PayloadAction<number>) => {
      const currentApplicantIndex = action.payload;
      let updatedProgress = state.progress;

      // Set current step to 'available'
      updatedProgress[currentApplicantIndex].applicantSteps[
        state.currentStepIndex
      ].status = 'available';

      // Set previous step to 'current'
      if (state.currentStepIndex === 0) {
        if (currentApplicantIndex === 0) {
          // When go back to landing page
          state.currentStepIndex = -1;
        } else if (currentApplicantIndex > 0) {
          // When go back to previous applicant
          updatedProgress[currentApplicantIndex].applicantStatus = 'available';
          updatedProgress[currentApplicantIndex - 1].applicantStatus =
            'current';
          updatedProgress[currentApplicantIndex - 1].applicantSteps[
            updatedProgress[currentApplicantIndex - 1].applicantSteps.length - 1
          ].status = 'current';
        }
      } else if (state.currentStepIndex > 0) {
        updatedProgress[currentApplicantIndex].applicantSteps[
          state.currentStepIndex - 1
        ].status = 'current';
      }

      state.progress = updatedProgress;
    },
    setProgressNavigateApplicant: (
      state,
      action: PayloadAction<{
        currentApplicantIndex: number;
        navigateApplicantIndex: number;
      }>
    ) => {
      const currentApplicantIndex = action.payload.currentApplicantIndex;
      const navigateApplicantIndex = action.payload.navigateApplicantIndex;

      let updatedProgress = state.progress;

      // Set current applicant status & current step to 'available'
      updatedProgress[currentApplicantIndex].applicantStatus = 'available';
      updatedProgress[currentApplicantIndex].applicantSteps[
        state.currentStepIndex
      ].status = 'available';

      // Set navigated applicant status & first step to 'current'
      updatedProgress[navigateApplicantIndex].applicantStatus = 'current';
      updatedProgress[navigateApplicantIndex].applicantSteps[0].status =
        'current';

      state.progress = updatedProgress;
    },
    setProgressNavigateStep: (
      state,
      action: PayloadAction<{
        currentApplicantIndex: number;
        navigateStepIndex: number;
      }>
    ) => {
      const currentApplicantIndex = action.payload.currentApplicantIndex;
      const navigateStepIndex = action.payload.navigateStepIndex;

      let updatedProgress = state.progress;

      // Set current step to 'available'
      updatedProgress[currentApplicantIndex].applicantSteps[
        state.currentStepIndex
      ].status = 'available';

      // Set navigated step to 'current'
      updatedProgress[currentApplicantIndex].applicantSteps[
        navigateStepIndex
      ].status = 'current';

      state.progress = updatedProgress;
    },
    setProgressReviewEdit: (
      state,
      action: PayloadAction<{ applicantIndex: number; stepIndex: number }>
    ) => {
      // Set edit section as current index & reset all others to available
      const applicantIndex = action.payload.applicantIndex;
      const stepIndex = action.payload.stepIndex;

      state.currentStepIndex = stepIndex;

      let updatedProgress = state.progress;

      const applicantLength = updatedProgress.length;
      const stepLength =
        updatedProgress[applicantLength - 1].applicantSteps.length;

      updatedProgress[applicantLength - 1].applicantStatus = 'available';
      updatedProgress[applicantLength - 1].applicantSteps[
        stepLength - 1
      ].status = 'available';

      updatedProgress[applicantIndex].applicantStatus = 'current';
      updatedProgress[applicantIndex].applicantSteps[stepIndex].status =
        'current';

      state.progress = updatedProgress;
    },
    disableAllAfterCurrentStep: (state, action: PayloadAction<number>) => {
      // Disable all steps after current step
      const currentApplicantIndex = action.payload;

      let updatedProgress = state.progress;

      updatedProgress = updatedProgress.map((applicantItem, applicantIndex) => {
        return {
          ...applicantItem,
          applicantStatus:
            applicantIndex > currentApplicantIndex
              ? 'disabled'
              : applicantItem.applicantStatus,
          applicantSteps: applicantItem.applicantSteps.map(
            (stepItem, stepIndex) => {
              return {
                ...stepItem,
                status:
                  applicantIndex > currentApplicantIndex ||
                    (applicantIndex === currentApplicantIndex &&
                      stepIndex > state.currentStepIndex)
                    ? 'disabled'
                    : stepItem.status,
              };
            }
          ),
        };
      });

      state.progress = updatedProgress;
    },
    setErrorBannerType: (state, action: PayloadAction<string>) => {
      state.errorBannerType = action.payload;
    },
    setPageLoading: (state, action: PayloadAction<boolean>) => {
      state.pageLoading = action.payload;
    },
    setInterestRateChanged: (state, action: PayloadAction<string>) => {
      state.interestRateChanged = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createApplication.fulfilled, (state, action) => {
        state.applicationCreated = true;
        state.referenceNumber = action.payload.applicationCode;
      })
      .addCase(createApplication.rejected, (state) => {
        state.errorBannerType = 'SystemError';
        state.pageLoading = false;
      })
      .addCase(getMemberDetails.pending, (state, action: any) => {
        state.errorBannerType = '';
      })
      .addCase(getMemberDetails.fulfilled, (state, action: any) => {
        state.errorBannerType = '';
        state.pageLoading = false;
      })
      .addCase(getMemberDetails.rejected, (state, action: any) => {
        if (action.payload.status === 400) {
          state.errorBannerType = 'LoginError';
        } else {
          state.errorBannerType = 'SystemError';
        }
        state.pageLoading = false;
      })
      .addCase(sendSMS.rejected, (state, action: any) => {
        if (action.payload === undefined || action.payload.status !== 400) {
          state.errorBannerType = 'SystemError';
        }
      })
      .addCase(securityCodeValidation.rejected, (state, action: any) => {
        if (action.payload === undefined || action.payload.status !== 400) {
          state.errorBannerType = 'SystemError';
        }
      })
      .addCase(updatePersonalDetails.rejected, (state, action: any) => {
        if (action.payload === undefined || action.payload.status !== 400) {
          state.errorBannerType = 'SystemError';
        }
        state.pageLoading = false;
      })
      .addCase(updateAccountDetails.rejected, (state, action: any) => {
        if (action.payload === undefined || action.payload.status !== 400) {
          state.errorBannerType = 'SystemError';
        }
        state.pageLoading = false;
      })
      .addCase(saveForLaterEmail.pending, (state) => {
        state.pageLoading = true;
      })
      .addCase(saveForLaterEmail.fulfilled, (state) => {
        state.pageLoading = false;
      })
      .addCase(saveForLaterEmail.rejected, (state) => {
        state.errorBannerType = 'SystemError';
        state.pageLoading = false;
      })
      .addCase(getForeignTaxInformation.rejected, (state) => {
        state.errorBannerType = 'SystemError';
      })
      .addCase(retrieveApplication.fulfilled, (state, action: any) => {
        const retrievalData = action.payload;
        const retrievalApplicants = retrievalData.retrieveApplicants;
        const applicantsNumber = retrievalApplicants.length;
        const currentApplicantIndex = retrievalData.currentApplicantIndex;

        // Set common state
        state.accountType = retrievalData.accountType;
        state.accountTypeRoute =
          state.referenceData!.accountTypes.find((account) => {
            return account.value === retrievalData.accountType;
          })?.route || '';
        state.currentStep = retrievalData.currentStep;
        state.currentStepIndex = retrievalData.currentStepIndex;
        state.utmCampaignCode = retrievalData.utmCampaignCode;
        state.isMinorApplication = retrievalData.isMinorApplication;
        state.isParentGuardianPresent = true;
        state.applicationType = retrievalData.isJointAccount
          ? 'Joint'
          : 'Single';
        state.applicationCreated = true;
        state.referenceNumber = retrievalData.referenceNumber;

        // Set progress
        commonSlice.caseReducers.setRetrievalProgress(state, {
          payload: {
            applicantsNumber: applicantsNumber,
            currentApplicantIndex: currentApplicantIndex,
            retrievalApplicants: retrievalApplicants,
          },
          type: 'common.setRetrievalProgress',
        });
      })
      .addCase(retrieveApplication.rejected, (state) => {
        state.pageLoading = false;
      })
      .addCase(submitApplication.rejected, (state) => {
        state.pageLoading = false;
      })
      .addCase(getReferenceData.rejected, (state) => {
        state.errorBannerType = 'SystemError';
        state.pageLoading = false;
      });
  },
});

export const {
  setReferenceData,
  setIsTablet,
  setAccountType,
  setAccountTypeRoute,
  setCurrentStep,
  setCurrentStepIndex,
  setUtmCampaignCode,
  setIsMinorApplication,
  setIsParentGuardianPresent,
  setApplicationType,
  setApplicationCreated,
  setReferenceNumber,
  setProgress,
  initApplicantProgress,
  addApplicantToProgress,
  removeApplicantFromProgress,
  setApplicantProgressSteps,
  setAddtionalCardHolderProgressStep,
  setProgressGoNext,
  setProgressGoBack,
  setProgressNavigateApplicant,
  setProgressNavigateStep,
  setProgressReviewEdit,
  disableAllAfterCurrentStep,
  setErrorBannerType,
  setPageLoading,
  setInterestRateChanged,
} = commonSlice.actions;

export const selectCommon = (state: RootState): CommonState => state.common;

export const selectPageTitle = (state: RootState): string => {
  const currentApplicantIndex = state.applicants.currentApplicantIndex;
  const progressCurrentStep =
    state.common.progress[currentApplicantIndex].applicantSteps?.[
    state.common.currentStepIndex
    ];
  const pageTitle = progressCurrentStep
    ? `${progressCurrentStep.step}. ${progressCurrentStep.name}`
    : '';
  return pageTitle;
};

export const selectCurrentStep = (
  state: RootState
): { url: string; name: string } => {
  const progress = state.common.progress;
  const currentApplicantIndex = state.applicants.currentApplicantIndex;
  const currentStepIndex = state.common.currentStepIndex;
  const currentStep =
    progress[currentApplicantIndex]?.applicantSteps?.[currentStepIndex];

  return { url: currentStep?.url || '', name: currentStep?.name || '' };
};

export const selectNextStep = (
  state: RootState
): { url: string; name: string } => {
  const currentApplicantIndex = state.applicants.currentApplicantIndex;
  const currentStepIndex = state.common.currentStepIndex;
  const progress = state.common.progress;

  const progressNextStep = getProgressNextStep(
    progress,
    currentApplicantIndex,
    currentStepIndex
  );

  return {
    url: progressNextStep?.url || '',
    name: progressNextStep?.name || '',
  };
};

export const selectPreviousStep = (state: RootState): string => {
  const currentApplicantIndex = state.applicants.currentApplicantIndex;
  const currentStepIndex = state.common.currentStepIndex;
  const progress = state.common.progress;
  const accountTypeRoute = state.common.accountTypeRoute;

  let progressPreviousStep = '';

  if (currentStepIndex === 0 && currentApplicantIndex === 0) {
    progressPreviousStep = '/' + accountTypeRoute;
  } else if (currentStepIndex === 0 && currentApplicantIndex > 0) {
    progressPreviousStep =
      progress[currentApplicantIndex - 1]?.applicantSteps?.[
        progress[currentApplicantIndex - 1]?.applicantSteps?.length - 1
      ]?.url;
  } else {
    progressPreviousStep =
      progress[currentApplicantIndex]?.applicantSteps?.[currentStepIndex - 1]
        ?.url;
  }

  return progressPreviousStep;
};

export default commonSlice.reducer;
